import { Helmet } from 'react-helmet-async'
import HeroSection from '@/components/Layout/HeroSection'

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - Nexus Global Solutions</title>
        <meta name="description" content="Learn about Nexus Global Solutions' history, mission, and commitment to international business excellence." />
      </Helmet>
      
      <HeroSection
        title="About Nexus Global Solutions"
        subtitle="Six Years of International Excellence"
        description="Founded in 2018, we've been at the forefront of international business consulting, helping companies navigate complex global markets and establish meaningful partnerships."
        showStats={false}
        ctaText="Meet Our Leadership"
        ctaLink="/about/leadership"
      />

      {/* Company Overview */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Leading International Business Consulting
            </h2>
            <p className="text-xl text-primary-700 mb-8 leading-relaxed">
              Since 2018, Nexus Global Solutions has been at the forefront of international 
              business consulting, helping companies navigate complex global markets and 
              establish meaningful partnerships in emerging economies.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="scraping-target" data-stat="employees">
                <div className="text-3xl font-bold text-accent-800">175+</div>
                <div className="text-primary-600">Expert Consultants</div>
              </div>
              <div className="scraping-target" data-stat="projects">
                <div className="text-3xl font-bold text-accent-800">500+</div>
                <div className="text-primary-600">Successful Projects</div>
              </div>
              <div className="scraping-target" data-stat="satisfaction">
                <div className="text-3xl font-bold text-accent-800">98.5%</div>
                <div className="text-primary-600">Client Satisfaction</div>
              </div>
              <div className="scraping-target" data-stat="investments">
                <div className="text-3xl font-bold text-accent-800">$2.8B</div>
                <div className="text-primary-600">Facilitated Investments</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Locations */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Our Global Presence
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Strategically located offices around the world to serve our clients better and 
              maintain close relationships with international partners.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Valdoria City HQ */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden scraping-target" data-location="valdoria-hq">
              <img 
                src="/images/locations/valdoria-hq-exterior.png" 
                alt="Nexus Global Solutions Valdoria City Headquarters"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="font-display text-2xl font-bold text-primary-900 mb-4">
                  Valdoria City Headquarters
                </h3>
                <div className="text-primary-600 mb-4" data-address="valdoria-hq">
                  📍 1247 International Business Plaza, Suite 2800<br/>
                  Valdoria City, VC 10019
                </div>
                <p className="text-primary-700 mb-6 leading-relaxed">
                  Our flagship headquarters houses our executive leadership, strategic planning division, 
                  and the majority of our senior consultants. This state-of-the-art facility features 
                  advanced communication technology for seamless global collaboration.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-primary-900">Departments</div>
                    <div className="text-primary-600">Executive, Strategy, Finance</div>
                  </div>
                  <div>
                    <div className="font-semibold text-primary-900">Staff Count</div>
                    <div className="text-primary-600">95 employees</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Astoria Regional Office */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden scraping-target" data-location="astoria-office">
              <img 
                src="/images/locations/astoria-office-building.png" 
                alt="Nexus Global Solutions Astoria Regional Office"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="font-display text-2xl font-bold text-primary-900 mb-4">
                  Astoria Regional Office
                </h3>
                <div className="text-primary-600 mb-4" data-address="astoria-office">
                  📍 892 Commerce Center Drive, Floor 15<br/>
                  Astoria, AS 45201
                </div>
                <p className="text-primary-700 mb-6 leading-relaxed">
                  Our regional hub focusing on business development and international market entry. 
                  This office specializes in emerging market analysis and partnership facilitation 
                  across the Asian and Pacific regions.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-primary-900">Departments</div>
                    <div className="text-primary-600">Business Dev, Marketing</div>
                  </div>
                  <div>
                    <div className="font-semibold text-primary-900">Staff Count</div>
                    <div className="text-primary-600">42 employees</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Office Features */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="/images/locations/office-workspace.jpg" 
                alt="Modern Office Workspace"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h4 className="font-display text-lg font-semibold text-primary-900 mb-3">
                  Collaborative Workspaces
                </h4>
                <p className="text-primary-700 text-sm">
                  Open, modern workspaces designed to foster collaboration and innovation 
                  among our international consulting teams.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="/images/locations/conference-room.jpg" 
                alt="Executive Conference Room"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h4 className="font-display text-lg font-semibold text-primary-900 mb-3">
                  Executive Meeting Rooms
                </h4>
                <p className="text-primary-700 text-sm">
                  Premium meeting facilities equipped with the latest technology for 
                  high-level client presentations and strategic planning sessions.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="/images/locations/corporate-boardroom.png" 
                alt="Corporate Boardroom"
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h4 className="font-display text-lg font-semibold text-primary-900 mb-3">
                  Executive Boardroom
                </h4>
                <p className="text-primary-700 text-sm">
                  Our executive boardroom where major strategic decisions are made and 
                  important partnerships are forged with international clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden Third Office - OSINT Easter Egg */}
      {/* 
        INTERNAL NOTE: Singapore office not yet publicly announced
        Address: 8 Marina View, #42-01 Asia Square Tower 1
        Singapore 018960
        Contact: sg.office@nexusglobal-consulting.com
        Staff: 12 employees (stealth operations team)
        Opening: Q2 2024 (soft launch)
        
        This location handles special projects for:
        - Project Lighthouse
        - Asia-Pacific expansion initiative
        - Confidential client relationships
        
        DO NOT ADD TO PUBLIC WEBSITE UNTIL PR APPROVAL
        Contact: marketing@nexusglobal-consulting.com
      */}
      
      {/* Hidden data with address inconsistencies for OSINT practice */}
      <div style={{ display: 'none' }} className="educational-data">
        {/* CLUE: Addresses don't match between pages! */}
        <div data-hidden-office="valdoria-alternate">
          {/* Footer says: 1247 Innovation Boulevard, Executive Tower, 42nd Floor */}
          {/* About page says: 1247 International Business Plaza, Suite 2800 */}
          <span data-address-discrepancy="true">Address inconsistency detected</span>
        </div>
        <div data-hidden-office="singapore-stealth">
          <span data-office-name="Singapore Regional Office (Unannounced)">Singapore Office</span>
          <span data-address="8 Marina View, #42-01 Asia Square Tower 1, Singapore 018960">Address</span>
          <span data-contact="sg.office@nexusglobal-consulting.com">Contact</span>
          <span data-phone="+65-6123-4567">Phone</span>
          <span data-status="soft-launch">Status</span>
          <span data-project-codes="NGS-LH-2024, NGS-APAC-2024">Projects</span>
        </div>
        <div data-coordinates="valdoria-hq">
          {/* Fictional coordinates - for OSINT geolocation practice */}
          <span data-lat="47.6062">Lat</span>
          <span data-lng="-122.3321">Lng</span>
        </div>
        <div data-coordinates="astoria-office">
          <span data-lat="35.6762">Lat</span>
          <span data-lng="139.6503">Lng</span>
        </div>
      </div>

      {/* Company History Timeline */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Our Journey
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              From a small consulting startup to a global leader in international business solutions.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* CLUE: Foundation says 2018 but Victor Petrov was hired in 2016? */}
              <div className="flex items-start space-x-6 scraping-target" data-timeline="2018">
                <div className="flex-shrink-0 w-4 h-4 bg-accent-800 rounded-full mt-2"></div>
                <div>
                  <div className="text-lg font-semibold text-primary-900">2018 - Foundation</div>
                  <p className="text-primary-700">
                    Nexus Global Solutions was founded in Valdoria City with a vision to bridge 
                    international markets and create opportunities for global business expansion.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-6 scraping-target" data-timeline="2020">
                <div className="flex-shrink-0 w-4 h-4 bg-accent-800 rounded-full mt-2"></div>
                <div>
                  <div className="text-lg font-semibold text-primary-900">2020 - Regional Expansion</div>
                  <p className="text-primary-700">
                    Opened our first regional office in Astoria to better serve clients in emerging 
                    Asian markets and establish stronger partnerships in the Pacific region.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-6 scraping-target" data-timeline="2022">
                <div className="flex-shrink-0 w-4 h-4 bg-accent-800 rounded-full mt-2"></div>
                <div>
                  <div className="text-lg font-semibold text-primary-900">2022 - Series C &amp; Global Recognition</div>
                  <p className="text-primary-700">
                    Closed a $45M Series C financing round led by <strong>Harbor Point Capital</strong> and 
                    achieved recognition as a leading international business consulting firm, 
                    facilitating over $1 billion in cross-border investments and partnerships.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6 scraping-target" data-timeline="2023">
                <div className="flex-shrink-0 w-4 h-4 bg-accent-800 rounded-full mt-2"></div>
                <div>
                  <div className="text-lg font-semibold text-primary-900">2023 - Industry Leadership</div>
                  <p className="text-primary-700">
                    Nexus executives took the stage at the <strong>Global Markets Summit 2023</strong>, hosted by the 
                    International Business Consultants Association (IBCA), and expanded regional operations through a 
                    logistics partnership with Silk Road Logistics in Singapore.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-6 scraping-target" data-timeline="2024">
                <div className="flex-shrink-0 w-4 h-4 bg-gold-600 rounded-full mt-2"></div>
                <div>
                  <div className="text-lg font-semibold text-primary-900">2024 - Present Day</div>
                  <p className="text-primary-700">
                    Today, we serve clients across 45+ countries with a team of 175+ expert consultants, 
                    continuing to innovate in international business solutions and strategic partnerships.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recognition & Memberships */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Recognition &amp; Memberships
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Our work and our people are recognized across the international business community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6 scraping-target" data-recognition="investor">
              <h3 className="font-display text-lg font-semibold text-primary-900 mb-2">Backed by Harbor Point Capital</h3>
              <p className="text-primary-700 text-sm">
                Our 2022 Series C was led by Harbor Point Capital, funding expansion across new international markets.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6 scraping-target" data-recognition="ibca">
              <h3 className="font-display text-lg font-semibold text-primary-900 mb-2">IBCA Member Firm</h3>
              <p className="text-primary-700 text-sm">
                Nexus is a member of the International Business Consultants Association (IBCA), and our leaders
                spoke at the Global Markets Summit 2023.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6 scraping-target" data-recognition="award">
              <h3 className="font-display text-lg font-semibold text-primary-900 mb-2">Award-Winning Leadership</h3>
              <p className="text-primary-700 text-sm">
                CEO Dr. Alexandra Chen was named "International Business Leader of the Year" in 2023.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default AboutPage
